<template>
  <div class="expand-to-2">
    <div>
      <h2 class="custom-title custom-color-gray font-medium mb-4">
        {{
          $t(
            'message.publishDocuments.configIndivualPublish.publishLoadDocument.question'
          )
        }}
      </h2>
      <p class="custom-legend text-zt-dark-300 mb-4">
        {{
          $t(
            'message.publishDocuments.configIndivualPublish.publishLoadDocument.description'
          )
        }}
      </p>
      <div class="details flex justify-start items-center mb-8">
        <zt-chip square outline>PDF</zt-chip>
        <zt-chip square outline>MAX 1MB</zt-chip>
      </div>
    </div>

    <div>
      <zt-input-file
        v-model="file"
        name="file"
        outlined
        append-icon="paperclip-2"
        filetypes=".pdf"
        :label="
          $t(
            'message.publishDocuments.configIndivualPublish.publishLoadDocument.label'
          )
        "
        :redIfError="errorMsg"
        v-validate="{
          required: true
        }"
        @change="onChangeInput"
        :key="reRenderKeyInput"
        data-vs-input="input-load-document"
      >
        <template slot="message">
          <span v-if="errorMsg" class="text-zt-danger">{{ errorMsg }}</span>
        </template>
      </zt-input-file>
      <div class="text-zt-danger" v-if="errors.has('file')">
        <span class="text-zt-danger">
          {{ errors.first('file') }}
        </span>
      </div>
      <div
        v-show="file"
        class="preview-file flex items-center justify-center text-base mt-4 py-2 cursor-pointer font-semibold border dark-700 rounded"
        @click="modalPreviewActive = true"
        data-vs-button="preview-document"
      >
        <zt-icon class="text-lg no-underline" name="eye" />
        <p class="ml-3 text-preview">
          {{
            $t(
              'message.publishDocuments.configIndivualPublish.publishLoadDocument.filePreview'
            )
          }}
        </p>
      </div>
    </div>
    <zt-modal
      class="modal-preview"
      :active="modalPreviewActive"
      :z-index="1000"
      @close="modalPreviewActive = false"
      @shown="shownModal"
    >
      <template slot="modal-header">
        <div class="p-4 text-center font-bold">
          <h4>
            {{
              $t(`message.publishDocuments.onlySign.modalPreviewDocument.title`)
            }}
          </h4>
        </div>
      </template>
      <template>
        <div class="relative">
          <div class="pdf-container" ref="containerPdf">
            <div class="w-full h-full absolute left-0 top-0">
              <zt-pdf-viewer
                :fixed-rotation="false"
                :minZoom="0.15"
                :page="curPage"
                :rotate="rotation"
                :scale="0.3"
                :src="base64"
                :key="reRenderKey"
                @viewer-update="normalizeRotation"
                @page-count="totalsPages = $event"
              />
            </div>
          </div>
          <div
            v-if="totalsPages > 0"
            class="w-full absolute bottom-0 flex justify-center items-center my-4"
          >
            <button
              @click="prevPage"
              data-vs-button="preview-document-prev-page"
            >
              <zt-icon name="angle-left" />
            </button>
            <div>{{ curPage }} de {{ totalsPages }}</div>
            <button
              @click="nextPage"
              data-vs-button="preview-document-next-page"
            >
              <zt-icon name="angle-right" />
            </button>
          </div>
        </div>
      </template>
      <template slot="modal-footer">
        <div
          class="flex justify-end items-center text-md p-4 bottom-0 w-full bg-zt-dark-200"
        >
          <zt-button
            @click="modalPreviewActive = false"
            data-vs-button="close-preview-document"
          >
            {{
              $t(
                `message.publishDocuments.onlySign.modalPreviewDocument.buttonClose`
              )
            }}
          </zt-button>
        </div>
      </template>
    </zt-modal>
  </div>
</template>

<script>
const MAX_FILE_SIZE = 1048576
const FILE_TYPE = 'application/pdf'

export default {
  name: 'PublishLoadDocument',
  inject: ['statePublish'],
  data() {
    return {
      errorMsg: '',
      file: '',
      base64: '',
      modalPreviewActive: false,
      rotation: 0,
      pdfLoaded: false,
      totalsPages: 0,
      curPage: 1,
      reRenderKey: 0,
      reRenderKeyInput: 0
    }
  },
  watch: {
    file(val) {
      if (!val) {
        this.errorMsg = ''
        this.clearFile()
      }
    }
  },
  methods: {
    onChangeInput(file) {
      if (file.size >= MAX_FILE_SIZE) {
        this.errorMsg = this.$t(
          'message.publishDocuments.configGenericPublish.maxFileSize'
        )
        return null
      }
      if (file.type !== FILE_TYPE) {
        this.errorMsg = this.$t(
          'message.publishDocuments.configGenericPublish.fileType'
        )
        return null
      }
      if (this.errorMsg) this.errorMsg = ''
      const reader = new FileReader()
      reader.onload = (event) => {
        this.base64 = event.target.result
      }
      reader.readAsDataURL(file)
      this.file = file
      this.statePublish.file = file
    },
    clearFile() {
      this.file = ''
      this.base64 = ''
      this.modalPreviewActive = false
      this.statePublish.file = ''
    },
    onClear() {
      this.clearFile()
      this.reRenderKeyInput += 1
    },
    normalizeRotation(doc) {
      this.pdfLoaded = true
      if (doc.page.pdfPageRotate > 0) {
        this.rotation = doc.page.pdfPageRotate * -1
      }
    },
    nextPage() {
      if (this.curPage < this.totalsPages) this.curPage++
    },
    prevPage() {
      if (this.curPage > 1) this.curPage--
    },
    shownModal() {
      this.$nextTick(() => {
        this.reRenderKey += 1
        this.curPage = 1
        this.totalsPages = 0
        this.pdfLoaded = false
        this.$refs.containerPdf.getBoundingClientRect()
      })
    }
  }
}
</script>

<style scoped lang="scss">
.pdf-container {
  width: 100%;
  height: 500px;
  position: relative;
  overflow: hidden;
}
.preview-file {
  width: 180px;
}
.dark-700 {
  color: #707070;
}
.details {
  ::v-deep {
    .zt-chip {
      background: #ebebeb !important;
    }
  }
}
::v-deep {
  .zt-input-appended-icon {
    font-size: 1.5rem !important;
  }

  .zt-input-text-field {
    .zt-chip {
      color: #261c77 !important;
      font-weight: 400 !important;
      padding-left: 0.5rem !important;
      padding-right: 0.5rem !important;
      background: #a7a0d1 !important;
    }
  }
  .zt-modal-body {
    border-radius: 0 !important;
  }
}
.custom-color-gray {
  color: #575757;
}
.custom-title {
  font-size: 1.5rem;
}
.custom-legend {
  font-size: 1.125rem;
}
</style>
